import React, { useState } from 'react';
import './RandomGenerator.css';

const RandomGenerator = () => {
  const [giveawayName, setGiveawayName] = useState('');
  const [namesText, setNamesText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [showWheel, setShowWheel] = useState(false);
  const [winner, setWinner] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [showWinner, setShowWinner] = useState(false);

  // Generate a color palette for names
  const generateColors = (count) => {
    const colors = [];
    const hueStep = 360 / count;
    for (let i = 0; i < count; i++) {
      const hue = (i * hueStep) % 360;
      colors.push(`hsl(${hue}, 70%, 50%)`);
    }
    return colors;
  };

  const handlePickWinner = () => {
    if (!giveawayName.trim()) {
      alert('Please enter a name for the giveaway');
      return;
    }

    const names = namesText
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (names.length === 0) {
      alert('Please enter at least one name');
      return;
    }

    // Pick random winner
    const randomIndex = Math.floor(Math.random() * names.length);
    const selectedWinner = names[randomIndex];
    setWinner(selectedWinner);

    // Show modal and start countdown
    setShowModal(true);
    setCountdown(10);
    setShowWheel(false);
    setIsSpinning(false);
    setShowWinner(false);

    // Countdown timer
    let currentCountdown = 10;
    const countdownInterval = setInterval(() => {
      currentCountdown--;
      setCountdown(currentCountdown);

      if (currentCountdown === 0) {
        clearInterval(countdownInterval);
        setShowWheel(true);
        setIsSpinning(true);

        // Stop spinning after animation and then show winner
        setTimeout(() => {
          setIsSpinning(false);
          setShowWinner(true);
        }, 3000);
      }
    }, 1000);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowWheel(false);
    setCountdown(10);
    setIsSpinning(false);
    setWinner('');
    setShowWinner(false);
  };

  return (
    <div className="random-generator-page">
      <div className="random-generator-container">
        <h1>The Shadow Kissed Draw</h1>
        
        <div className="form-group">
          <label htmlFor="giveaway-name">Name of the give away</label>
          <input
            id="giveaway-name"
            type="text"
            value={giveawayName}
            onChange={(e) => setGiveawayName(e.target.value)}
            placeholder="Enter giveaway name"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="names-textarea">Participants (one per row)</label>
          <textarea
            id="names-textarea"
            value={namesText}
            onChange={(e) => setNamesText(e.target.value)}
            placeholder="Enter names, one per row"
            className="form-textarea"
            rows="10"
          />
        </div>

        <button 
          className="pick-winner-btn"
          onClick={handlePickWinner}
        >
          Pick Random Winner
        </button>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className={`modal-content ${showWheel ? 'fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>×</button>
            
            <h2 className="modal-title">{giveawayName}</h2>

            {!showWheel ? (
              <div className="countdown-container">
                <div className="countdown-number">{countdown}</div>
                <div className="countdown-label">Get ready!</div>
              </div>
            ) : (
              <div className="wheel-container">
                <div className="wheel-wrapper">
                  {(() => {
                    const names = namesText
                      .split('\n')
                      .map(name => name.trim())
                      .filter(name => name.length > 0);
                    const colors = generateColors(names.length);
                    // Calculate wheel size: use full display when in fullscreen mode
                    const baseSize = 400;
                    const sizePerName = 25;
                    // Account for modal padding and other elements
                    const modalPadding = 80; // 40px padding on each side
                    const otherElementsHeight = 150; // Title, winner display, etc.
                    // Use full viewport dimensions when wheel is shown (fullscreen mode)
                    const maxSize = typeof window !== 'undefined' 
                      ? Math.min(
                          Math.max(window.innerWidth, window.innerHeight) * 0.9, 
                          (window.innerHeight - 100) * 0.95,
                          (window.innerWidth - 40) * 0.95
                        )
                      : 600;
                    const wheelSize = Math.min(maxSize, Math.max(baseSize, names.length * sizePerName));
                    const radius = wheelSize / 2 - 80; // More space from center for longer names
                    const centerX = wheelSize / 2;
                    const centerY = wheelSize / 2;
                    // Dynamic font size based on number of names
                    const fontSize = Math.max(12, Math.min(18, 20 - names.length / 5));
                    
                    return (
                      <div 
                        className={`spinning-wheel ${isSpinning ? 'spinning' : ''}`}
                        style={{ 
                          width: `${wheelSize}px`,
                          height: `${wheelSize}px`,
                          maxWidth: '100%',
                          maxHeight: '100%',
                          background: `conic-gradient(${names.map((_, i) => {
                            const startAngle = (i / names.length) * 360;
                            const endAngle = ((i + 1) / names.length) * 360;
                            return `${colors[i]} ${startAngle}deg ${endAngle}deg`;
                          }).join(', ')})`
                        }}
                      >
                        <div className="wheel-names">
                          {names.map((name, i) => {
                            const angle = (i / names.length) * 360 - 90;
                            const angleRad = (angle * Math.PI) / 180;
                            const x = centerX + radius * Math.cos(angleRad);
                            const y = centerY + radius * Math.sin(angleRad);
                            return (
                              <div 
                                key={i} 
                                className="wheel-name"
                                title={name}
                                data-rotation={angle + 90}
                                style={{ 
                                  left: `${x}px`,
                                  top: `${y}px`,
                                  color: colors[i],
                                  fontSize: `${fontSize}px`,
                                  transform: `translate(-50%, -50%) rotate(${angle + 90}deg)`
                                }}
                              >
                                {name}
                              </div>
                            );
                          })}
                        </div>
                        <div className="wheel-center">
                          <div className="wheel-pointer"></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {showWinner && winner && (
                  <div className="winner-display">
                    <div className="winner-label">Winner:</div>
                    <div className="winner-name">{winner}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RandomGenerator;

